import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useFullPageStore, FULLPAGE_SECTIONS } from '../../stores/useFullPageStore';
import { SideDots } from './SideDots';
import './FullPage.css';

interface FullPageProps {
  children: React.ReactNode[];
}

/**
 * 满屏 fullpage 切换容器
 * - 桌面：滚轮 / trackpad / 方向键 / PageUp/Down / Home / End 切换
 * - 移动：CSS scroll-snap fallback
 * - 智能豁免：section 内部有 .fp-scroll 且未滚到边界时优先滚内部
 * - 屏底 "继续下滑" 提示: 内部还能滚时显示
 */
export const FullPage: React.FC<FullPageProps> = ({ children }) => {
  const index = useFullPageStore((s) => s.index);
  const isAnimating = useFullPageStore((s) => s.isAnimating);
  const setIndex = useFullPageStore((s) => s.setIndex);
  const next = useFullPageStore((s) => s.next);
  const prev = useFullPageStore((s) => s.prev);

  const containerRef = useRef<HTMLDivElement>(null);
  const wheelAccumulator = useRef(0);
  const wheelDebounceTimer = useRef<number | null>(null);
  const lastWheelAt = useRef(0);

  /** 到达内部滚动边界后, 需要"停顿"才允许切换 section, 避免滚轮惯性划过下一屏 */
  const lastInternalScrollAtRef = useRef(0);
  const EDGE_HOLD_MS = 500;

  const isMobile = useRef(typeof window !== 'undefined' && window.innerWidth < 900);

  const [scrollAtBottom, setScrollAtBottom] = useState<boolean[]>(() =>
    FULLPAGE_SECTIONS.map(() => false)
  );

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      const idx = FULLPAGE_SECTIONS.findIndex((s) => s.id === hash);
      if (idx >= 0) setIndex(idx, 'init');
    }
  }, [setIndex]);

  useEffect(() => {
    const target = FULLPAGE_SECTIONS[index];
    if (target && window.location.hash !== `#${target.id}`) {
      history.replaceState(null, '', `#${target.id}`);
    }
    window.dispatchEvent(new CustomEvent('fullpage:change', { detail: { index, id: target?.id } }));

    // 切到新 section 时重置 fp-scroll 到顶
    const section = containerRef.current?.querySelectorAll('.fp-section')[index] as HTMLElement | undefined;
    section?.querySelector<HTMLElement>('.fp-scroll')?.scrollTo({ top: 0, behavior: 'auto' });
  }, [index]);

  /** 监听每个 section 的 scrollAtBottom */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const sections = [...container.querySelectorAll<HTMLElement>('.fp-section')];
    const cleanups: Array<() => void> = [];

    sections.forEach((sec, i) => {
      const scrollEl = sec.querySelector<HTMLElement>('.fp-scroll');
      if (!scrollEl) {
        setScrollAtBottom((prev) => {
          if (prev[i] === true) return prev;
          const nx = [...prev]; nx[i] = true; return nx;
        });
        return;
      }
      const check = () => {
        const overflowing = scrollEl.scrollHeight > scrollEl.clientHeight + 4;
        const atBottom = !overflowing || scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 4;
        setScrollAtBottom((prev) => {
          if (prev[i] === atBottom) return prev;
          const nx = [...prev]; nx[i] = atBottom; return nx;
        });
      };
      check();
      scrollEl.addEventListener('scroll', check, { passive: true });
      cleanups.push(() => scrollEl.removeEventListener('scroll', check));
    });
    return () => cleanups.forEach((fn) => fn());
  }, [index, children]);

  const isInternalScrollEligible = useCallback((deltaY: number): boolean => {
    const section = containerRef.current?.querySelectorAll('.fp-section')[index] as HTMLElement | undefined;
    if (!section) return false;
    const scrollables = [...section.querySelectorAll<HTMLElement>('.fp-scroll')].filter(
      (el) => el.scrollHeight > el.clientHeight + 4
    );
    for (const el of scrollables) {
      const atTop = el.scrollTop <= 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      if (deltaY > 0 && !atBottom) return true;
      if (deltaY < 0 && !atTop) return true;
    }
    return false;
  }, [index]);

  useEffect(() => {
    if (isMobile.current) return;
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.wiki-modal, .cert-modal, .case-modal, [role="dialog"]')) return;

      // 内部还能滚 → 让浏览器滚 fp-scroll, 同时记录"最近一次内部滚动时间"
      if (isInternalScrollEligible(e.deltaY)) {
        lastInternalScrollAtRef.current = Date.now();
        return;
      }

      e.preventDefault();
      if (isAnimating) return;

      // 缓冲窗口: 刚从内部滚动到边界(距上次内部滚动 < EDGE_HOLD_MS) 时不切页,
      // 强迫用户"停一下再滚"才切换, 避免连续滚轮惯性划过整屏内容
      const sinceEdge = Date.now() - lastInternalScrollAtRef.current;
      if (sinceEdge < EDGE_HOLD_MS) {
        wheelAccumulator.current = 0;
        return;
      }

      const now = Date.now();
      if (now - lastWheelAt.current > 250) wheelAccumulator.current = 0;
      lastWheelAt.current = now;
      wheelAccumulator.current += e.deltaY;

      const THRESHOLD = 60;
      if (Math.abs(wheelAccumulator.current) < THRESHOLD) return;

      if (wheelAccumulator.current > 0) next();
      else prev();
      wheelAccumulator.current = 0;

      if (wheelDebounceTimer.current) clearTimeout(wheelDebounceTimer.current);
      wheelDebounceTimer.current = window.setTimeout(() => (wheelAccumulator.current = 0), 200);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel as EventListener);
  }, [isAnimating, isInternalScrollEligible, next, prev]);

  useEffect(() => {
    if (isMobile.current) return;
    const onKey = (e: KeyboardEvent) => {
      if (isAnimating) return;
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (document.querySelector('.wiki-modal, .cert-modal, .case-modal, [role="dialog"]')) return;

      switch (e.key) {
        case 'ArrowDown':
        case 'PageDown':
        case ' ':
          if (isInternalScrollEligible(1)) return;
          e.preventDefault();
          next();
          break;
        case 'ArrowUp':
        case 'PageUp':
          if (isInternalScrollEligible(-1)) return;
          e.preventDefault();
          prev();
          break;
        case 'Home':
          e.preventDefault();
          setIndex(0, 'key');
          break;
        case 'End':
          e.preventDefault();
          setIndex(FULLPAGE_SECTIONS.length - 1, 'key');
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isAnimating, isInternalScrollEligible, next, prev, setIndex]);

  useEffect(() => {
    if (!isMobile.current) return;
    const el = containerRef.current;
    if (!el) return;

    let touchStartY = 0;
    let touchStartTime = 0;

    const onStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
      touchStartTime = Date.now();
    };
    const onEnd = (e: TouchEvent) => {
      if (isAnimating) return;
      const dy = touchStartY - e.changedTouches[0].clientY;
      const dt = Date.now() - touchStartTime;
      if (dt > 500) return;
      if (Math.abs(dy) < 60) return;
      if (isInternalScrollEligible(dy)) return;
      dy > 0 ? next() : prev();
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
    };
  }, [isAnimating, isInternalScrollEligible, next, prev]);

  return (
    <div
      ref={containerRef}
      className={`fp-root ${isMobile.current ? 'fp-mobile' : ''}`}
      data-fp-index={index}
    >
      <div
        className="fp-track"
        style={{
          transform: isMobile.current ? undefined : `translate3d(0, -${index * 100}vh, 0)`,
        }}
      >
        {children.map((child, i) => {
          const showHint =
            !isMobile.current &&
            index === i &&
            !scrollAtBottom[i] &&
            i < FULLPAGE_SECTIONS.length - 1;
          return (
            <section
              key={FULLPAGE_SECTIONS[i]?.id ?? i}
              id={FULLPAGE_SECTIONS[i]?.id}
              className={`fp-section ${index === i ? 'fp-active' : ''}`}
            >
              {child}
              {showHint && (
                <div className="fp-scroll-hint">
                  <i className="fa-solid fa-chevron-down"></i>
                  <span>继续下滑</span>
                </div>
              )}
            </section>
          );
        })}
      </div>

      <SideDots />
    </div>
  );
};

export default FullPage;
